/** Persistencia UTM en el navegador. Un solo objeto: campaña activa + sesión de tracking. */

export const UTM_STORAGE_KEY = 'neocredito-utm-activa';
/** @deprecated Claves antiguas; se migran al leer y se eliminan. */
const LEGACY_SESSION_KEY = 'neocredito-utm-sesion';
const LEGACY_INGRESO_KEY = 'neocredito-utm-ingreso-firma';
export const UTM_INICIO_REGISTRO_KEY = 'neocredito-utm-inicio-registro';

/** Solo lo necesario en cliente; params y URL vienen del catálogo NEO_UTM (idUtmCatalogo). */
export interface UtmStorageState {
  idUtmCatalogo?: number;
  /** Último idUtm con el que ya se registró INGRESO_CANAL (evita duplicados). */
  ultimoIngresoIdUtm?: number;
  /** Correlación de eventos anónimos → enriquecer tras login. */
  idSesionTracking?: string;
}

/** Params parseados de la URL (solo para resolver contra catálogo; no se persisten). */
export interface UtmUrlParams {
  utmId?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
}

export function hasUtmActiva(state?: UtmStorageState | null): boolean {
  return !!(state?.idUtmCatalogo && state.idUtmCatalogo > 0);
}

export function readUtmStorage(): UtmStorageState {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    let state: UtmStorageState = {};
    let migrated = false;
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const legacyId = parsed.idUtmCatalogo ?? parsed['idUtm'];
      if (legacyId != null && Number(legacyId) > 0) {
        state.idUtmCatalogo = Number(legacyId);
        if (parsed.utmSource || parsed.utmCampaign) {
          migrated = true;
        }
      }
      if (parsed.ultimoIngresoIdUtm != null && Number(parsed.ultimoIngresoIdUtm) > 0) {
        state.ultimoIngresoIdUtm = Number(parsed.ultimoIngresoIdUtm);
      }
      if (typeof parsed.idSesionTracking === 'string' && parsed.idSesionTracking) {
        state.idSesionTracking = parsed.idSesionTracking;
      }
    }

    const legacySession = localStorage.getItem(LEGACY_SESSION_KEY);
    if (legacySession && !state.idSesionTracking) {
      state.idSesionTracking = legacySession;
      localStorage.removeItem(LEGACY_SESSION_KEY);
      migrated = true;
    }

    const legacyIngreso = localStorage.getItem(LEGACY_INGRESO_KEY);
    if (legacyIngreso) {
      if (state.idUtmCatalogo && !state.ultimoIngresoIdUtm) {
        state.ultimoIngresoIdUtm = state.idUtmCatalogo;
      }
      localStorage.removeItem(LEGACY_INGRESO_KEY);
      migrated = true;
    }

    if (migrated) {
      persistUtmStorage(state);
    }

    return state;
  } catch {
    return {};
  }
}

function persistUtmStorage(state: UtmStorageState): void {
  try {
    const payload: UtmStorageState = {};
    if (state.idUtmCatalogo && state.idUtmCatalogo > 0) {
      payload.idUtmCatalogo = state.idUtmCatalogo;
    }
    if (state.ultimoIngresoIdUtm && state.ultimoIngresoIdUtm > 0) {
      payload.ultimoIngresoIdUtm = state.ultimoIngresoIdUtm;
    }
    if (state.idSesionTracking) {
      payload.idSesionTracking = state.idSesionTracking;
    }
    if (!payload.idUtmCatalogo && !payload.idSesionTracking && !payload.ultimoIngresoIdUtm) {
      localStorage.removeItem(UTM_STORAGE_KEY);
      return;
    }
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

export function writeUtmCatalogo(idUtmCatalogo: number): void {
  const state = readUtmStorage();
  state.idUtmCatalogo = idUtmCatalogo;
  persistUtmStorage(state);
}

export function clearUtmCatalogo(): void {
  const state = readUtmStorage();
  delete state.idUtmCatalogo;
  delete state.ultimoIngresoIdUtm;
  persistUtmStorage(state);
  try {
    sessionStorage.removeItem(UTM_INICIO_REGISTRO_KEY);
  } catch {
    // ignore
  }
}

export function debeRegistrarIngreso(idUtmCatalogo: number): boolean {
  const state = readUtmStorage();
  return state.ultimoIngresoIdUtm !== idUtmCatalogo;
}

export function marcarIngresoRegistrado(idUtmCatalogo: number): void {
  const state = readUtmStorage();
  state.idUtmCatalogo = idUtmCatalogo;
  state.ultimoIngresoIdUtm = idUtmCatalogo;
  persistUtmStorage(state);
}

/** Libera el anti-duplicado si falló el POST de INGRESO_CANAL (permite reintento). */
export function liberarMarcaIngreso(idUtmCatalogo: number): void {
  const state = readUtmStorage();
  if (state.ultimoIngresoIdUtm === idUtmCatalogo) {
    delete state.ultimoIngresoIdUtm;
    persistUtmStorage(state);
  }
}

export function getOrCreateUtmSessionId(): string {
  const state = readUtmStorage();
  if (state.idSesionTracking) {
    return state.idSesionTracking;
  }
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `utm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  state.idSesionTracking = id;
  persistUtmStorage(state);
  return id;
}

export function readIdUtmCatalogo(): number | undefined {
  const id = readUtmStorage().idUtmCatalogo;
  return id && id > 0 ? id : undefined;
}

export function wasInicioRegistroMarked(): boolean {
  try {
    return sessionStorage.getItem(UTM_INICIO_REGISTRO_KEY) === '1';
  } catch {
    return false;
  }
}

export function markInicioRegistro(): void {
  try {
    sessionStorage.setItem(UTM_INICIO_REGISTRO_KEY, '1');
  } catch {
    // ignore
  }
}

/** Lee utm_* del query string (solo para match con catálogo BX). */
export function parseUtmFromSearch(
  search: string | { get(name: string): string | null }
): UtmUrlParams | null {
  const get =
    typeof search === 'string'
      ? (name: string) => new URLSearchParams(search.startsWith('?') ? search : `?${search}`).get(name)
      : (name: string) => search.get(name);

  const params: UtmUrlParams = {
    utmId: trimOrUndef(get('utm_id')),
    utmSource: trimOrUndef(get('utm_source')),
    utmMedium: trimOrUndef(get('utm_medium')),
    utmCampaign: trimOrUndef(get('utm_campaign')),
    utmContent: trimOrUndef(get('utm_content')),
  };
  return hasUtmUrlParams(params) ? params : null;
}

export function hasUtmUrlParams(params: UtmUrlParams | null | undefined): boolean {
  if (!params) {
    return false;
  }
  return !!(
    params.utmSource ||
    params.utmMedium ||
    params.utmCampaign ||
    params.utmContent ||
    params.utmId
  );
}

function trimOrUndef(v: string | null | undefined): string | undefined {
  if (v == null) {
    return undefined;
  }
  const t = String(v).trim();
  return t ? t : undefined;
}
